import { Analysis, PossibleRootCauseCategory, ProbabilityCategory } from '.';
import { MathUtils } from '../utils/MathUtils';

/**
 * Stress & Adrenal Balance analysis.
 *
 * First cut (MVP): a single-marker interpretation per marker, mirroring the
 * Electrolytes/Hormones style, plus a bidirectional Na/K-ratio gauge. The
 * markers themselves are MOVED here (display + interpretation):
 *   • Na/K ratio        — from Electrolytes (was 9032/9034)
 *   • Cortisol AM/PM    — from Hormones (was 15000/15010 / 15020/15030)
 *   • DHEA-S            — from Hormones (was 15040/15050)
 *   • Pregnenolone      — from Hormones (was 15220/15230)
 *
 * Possible root-cause CONTENT lives in the FurtherConsiderations DB keyed to the
 * 90xxx categoryIds (same as the thyroid pattern categories), to be populated
 * later. A full physiology-area pattern set (like thyroid's) is a planned
 * follow-up that drops onto this scaffold.
 */

/**
 * Bidirectional adrenal-axis gauge for the Na/K ratio, shaped like the Na/K
 * reference bar (green in the MIDDLE, severity colors out to both ends):
 *   • below optimal → adrenal INSUFFICIENCY (low aldosterone-type pattern) — left
 *   • above optimal → adrenal HYPERFUNCTION / excess — right
 *   • within optimal → balanced — centre
 * The needle is a 0–100 position on a symmetric 7-band arc (50 = optimal centre),
 * NOT a probability — so it doesn't read like the glucose likelihood gauge.
 * Band breakpoints (mirror NAK_GAUGE_STOPS in StressAdrenalGauge):
 *   red 0–12 · orange 12–28 · yellow 28–43 · GREEN 43–57 · yellow 57–72 · orange 72–88 · red 88–100.
 */
export type AdrenalCondition = 'insufficiency' | 'hyperfunction' | 'balanced';

/**
 * A labelled scale tick on the outer edge of the dial. Each boundary sits at a
 * FIXED needle position (the band breakpoints) and carries the real Na/K ratio
 * threshold at that breakpoint, so the dial reads as a quantitative scale:
 *   • alarmMin → 12, labMin → 28, optimalMin → 43,
 *     optimalMax → 57, labMax → 72, alarmMax → 88.
 * The synthetic 0/100 ends are deliberately NOT ticked — they are extrapolated
 * bounds with no real clinical threshold.
 */
export interface AdrenalTick {
  /** 0–100 dial position (a fixed band boundary). */
  needle: number;
  /** Na/K ratio value at this boundary. */
  value: number;
  /** value formatted to the marker's display precision. */
  label: string;
  /** True for the optimal-range edges (the green band) — emphasised in the UI. */
  emphasis: boolean;
}

export interface AdrenalGauge {
  /** Needle position 0–100 on the bidirectional dial. 50 = optimal centre;
   *  toward 0 = increasingly low (insufficiency); toward 100 = high (excess). */
  needle: number;
  category: ProbabilityCategory;
  /** Names the pattern (low vs high) and selects the gauge labels. */
  condition: AdrenalCondition;
  /** Raw Na/K ratio value (the insights panel shows the marker's displayValue). */
  ratioValue: number;
  /** Outer-edge scale ticks (optimal + lab edges) for the dial. */
  ticks: AdrenalTick[];
}

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/** Linear map of x in [x0,x1] onto [y0,y1]; falls back to the zone midpoint if the
 *  span collapses (avoids NaN when a range bound is missing/equal). */
const lerp = (x: number, x0: number, x1: number, y0: number, y1: number) =>
  x1 === x0 ? (y0 + y1) / 2 : y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);

/**
 * Map the Na/K ratio marker to a bidirectional adrenal-axis gauge state. Pure +
 * null-safe so it can be unit tested and reused by the insights component.
 * Returns null when unmeasured or range-less.
 */
export function adrenalGauge(marker?: any): AdrenalGauge | null {
  if (!marker?.hasValue?.() || !marker.range) return null;
  const r = marker.range;
  const v = marker.value;
  const { optimalMin, optimalMax, labMin, labMax } = r;
  const alarmMin = r.alarmMin ?? labMin; // fall back if no alarm bound defined
  const alarmMax = r.alarmMax ?? labMax;

  let needle: number;
  let category: ProbabilityCategory;
  let condition: AdrenalCondition;

  if (v < optimalMin) {
    // ── Below optimal → adrenal insufficiency (left of centre) ──
    condition = 'insufficiency';
    if (v >= labMin) {
      category = ProbabilityCategory.POSSIBLE;
      needle = lerp(v, optimalMin, labMin, 43, 28); // yellow (low)
    } else if (v >= alarmMin) {
      category = ProbabilityCategory.LIKELY;
      needle = lerp(v, labMin, alarmMin, 28, 12); // orange (low)
    } else {
      category = ProbabilityCategory.HIGHLY_LIKELY;
      needle = lerp(v, alarmMin, alarmMin - (labMin - alarmMin), 12, 0); // red (low)
    }
  } else if (v > optimalMax) {
    // ── Above optimal → adrenal hyperfunction (right of centre) ──
    condition = 'hyperfunction';
    if (v <= labMax) {
      category = ProbabilityCategory.POSSIBLE;
      needle = lerp(v, optimalMax, labMax, 57, 72); // yellow (high)
    } else if (v <= alarmMax) {
      category = ProbabilityCategory.LIKELY;
      needle = lerp(v, labMax, alarmMax, 72, 88); // orange (high)
    } else {
      category = ProbabilityCategory.HIGHLY_LIKELY;
      needle = lerp(v, alarmMax, alarmMax + (alarmMax - labMax), 88, 100); // red (high)
    }
  } else {
    // ── Within optimal → balanced (green centre) ──
    condition = 'balanced';
    category = ProbabilityCategory.LESS_LIKELY;
    needle = lerp(v, optimalMin, optimalMax, 43, 57);
  }

  // Outer-edge scale ticks. Each boundary sits at a fixed needle position; the
  // value is the real Na/K threshold from the range, formatted to the marker's
  // display precision so it matches the readout below the dial. Optimal/lab
  // bounds can be undefined for pediatric ranges — those ticks are dropped.
  const precision = marker.field?.precision ?? 1;
  const fmt = (n: number) => '' + MathUtils.round(n, precision);
  // Alarm edges use the RAW range bounds (not the labMin/labMax fallbacks used
  // for the needle math), so ranges without alarm thresholds simply drop them.
  const ticks: AdrenalTick[] = (
    [
      { value: r.alarmMin, needle: 12, emphasis: false },
      { value: labMin, needle: 28, emphasis: false },
      { value: optimalMin, needle: 43, emphasis: true },
      { value: optimalMax, needle: 57, emphasis: true },
      { value: labMax, needle: 72, emphasis: false },
      { value: r.alarmMax, needle: 88, emphasis: false },
    ] as Array<{ value: any; needle: number; emphasis: boolean }>
  )
    .filter(t => typeof t.value === 'number' && isFinite(t.value))
    .map(t => ({ needle: t.needle, value: t.value, label: fmt(t.value), emphasis: t.emphasis }));

  return { needle: clamp(needle, 0, 100), category, condition, ratioValue: v, ticks };
}

export class StressAdrenalAnalysis extends Analysis {
  calculateInsights() {
    // Na/K ratio (moved from Electrolytes, was 9032/9034)
    if (this.ba.sodiumPotassiumRatio.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90000);
    } else if (this.ba.sodiumPotassiumRatio.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90010);
    }

    // Sodium out of optimal (either direction) → see Electrolytes section
    if (this.ba.sodium.isNotOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90012);
    }

    // Potassium out of optimal (either direction) → see Electrolytes section
    if (this.ba.potassium.isNotOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90014);
    }

    // Cortisol AM (moved from Hormones, was 15000/15010)
    if (this.ba.cortisolAM.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90015);
    } else if (this.ba.cortisolAM.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90020);
    }

    // Cortisol PM (moved from Hormones, was 15020/15030)
    if (this.ba.cortisolPM.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90025);
    } else if (this.ba.cortisolPM.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90030);
    }

    // DHEA-S (moved from Hormones, was 15040/15050)
    if (this.ba.dheaS.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90035);
    } else if (this.ba.dheaS.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90040);
    }

    // Pregnenolone (moved from Hormones, was 15220/15230)
    if (this.ba.pregnenolone.isGreaterThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90080);
    } else if (this.ba.pregnenolone.isStricklyLessThanOptimal()) {
      this.possibleRootCauseCategories.push(PossibleRootCauseCategory.CATEGORY_90090);
    }
  }

  /** Bidirectional adrenal-axis gauge for the Na/K ratio (null if unmeasured). */
  adrenalGaugeState(): AdrenalGauge | null {
    return adrenalGauge(this.ba.sodiumPotassiumRatio);
  }
}
